import type { InlineReferenceEditorHandle } from '@crowdex/ui/components/inline-reference-editor';
import type { RefObject } from 'react';
import type { MentionTarget } from '@/components/desktop-side-panel';
import {
  createSelectionActions,
  type SelectionActions,
} from '@/lib/desktop-shell/selection-actions';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useComposerDerivations } from '@/lib/desktop-shell/use-composer-derivations';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import { mentionArtifact } from '@/lib/side-panel/mention-targets';
import { showPanelView } from '@/lib/side-panel/panel-tabs';

type Derivations = ReturnType<typeof useComposerDerivations>;

export type ConversationController = Derivations &
  SelectionActions & {
    insertArtifact: (target: MentionTarget) => void;
    insertMention: (target: MentionTarget) => void;
    insertSkill: (skill: AvailablePluginSkill) => void;
  };

/**
 * The per-conversation composer machinery, assembled from the pure derivation
 * hook plus the store-backed selection actions and the reference-insertion
 * helpers. This is the reusable brain a conversation pane needs to drive its
 * composer's picker; the active pane and split panes construct one each with
 * their own draft/connection so they share behaviour without sharing state.
 */
export function useConversationController(input: {
  store: DesktopShellStore;
  composerRef: RefObject<InlineReferenceEditorHandle | null>;
  composerText: string;
  composerContextTargets: readonly MentionTarget[];
}): ConversationController {
  const derivations = useComposerDerivations({
    composerText: input.composerText,
    composerContextTargets: input.composerContextTargets,
  });
  const selection = createSelectionActions({ store: input.store });

  function insertMention(target: MentionTarget): void {
    input.composerRef.current?.insertReference(
      target,
      (derivations.mentionQuery ?? derivations.commandQuery)?.length ?? 0,
    );
    // A referenced artifact opens beside the conversation.
    const artifact = mentionArtifact(target);
    if (artifact !== null) {
      const state = input.store.getState();
      state.setPanelTabs((tabs) => showPanelView(tabs, `artifact:${artifact.id}`));
      state.setSidePanelOpen(true);
    }
  }

  function insertArtifact(target: MentionTarget): void {
    input.composerRef.current?.insertReferenceAtSelection(target);
  }

  function insertSkill(skill: AvailablePluginSkill): void {
    input.composerRef.current?.insertReference(
      { id: skill.uri, kind: 'skill', label: skill.name },
      (derivations.commandQuery ?? derivations.skillQuery)?.length ?? 0,
    );
  }

  return { ...derivations, ...selection, insertArtifact, insertMention, insertSkill };
}
